import React, { useEffect, useState, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Formulario, { FormField } from "../../../components/Formulario";
import {
  createTipoUsuario,
  getUsuarioById,
  updateUsuario,
} from "../../../api/usuariosApi";
import { getPessoas, Pessoa, vincularPessoa } from "../../../api/atendimentoApi";
import {
  Autocomplete,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { Empresa, Tipo } from "../../../types/usuarios";
import { getEmpresas } from "../../../api/empresaApi";
import { deleteTipoUser, getTipo } from "../../../api/tipoApi";

const EditarUsuario: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [nomeUsuario, setNomeUsuario] = useState<string>("");
  const [loginUsuario, setLoginUsuario] = useState<string>("");
  const [assinaturaUsuario, setAssinaturaUsuario] = useState<string>("");
  const [senhaUsuario, setSenhaUsuario] = useState<string>("");
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [idEmpresa, setIdEmpresa] = useState<number>(1);
  const [tiposUsuarioSelecionados, setTiposUsuarioSelecionados] = useState<
  Tipo[]
  >([]);
  const [tiposUsuario, setTiposUsuario] = useState<Tipo[]>([]);
  const [allTipos, setAllTipos] = useState<Tipo[]>([]);
  const [tipo, setTipo] = useState<Tipo[]>([]);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null);
  const [isDealer, setIsDealer] = useState<boolean>(false);
  const [hasPessoa, setHasPessoa] = useState<boolean>(false);
  const [linking, setLinking] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [telefoneUsuario, setTelefoneUsuario] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsuario = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const usuarioData = await getUsuarioById(token, id!);
        setNomeUsuario(usuarioData.nome_usuario);
        setLoginUsuario(usuarioData.login_usuario);
        setIdEmpresa(usuarioData.id_empresa);
        setAssinaturaUsuario(usuarioData.assinatura_usuario);
        setTipo(usuarioData.tipo);
        setEmpresa(usuarioData.empresa);
        setTelefoneUsuario(usuarioData.telefone_usuario);
        setHasPessoa(Boolean(usuarioData.tem_pessoa));
        if (
          !usuarioData.tem_pessoa &&
          usuarioData.empresa &&
          usuarioData.empresa.tipo_dms &&
          usuarioData.empresa.tipo_dms.descricao_tipo_dms === "Dealer"
        ) {
          setIsDealer(true);
          try {
            const pessoas0 = await getPessoas(token, 0, usuarioData.id);
            const pessoas1 = await getPessoas(token, 1, usuarioData.id);
            const merged = [...(pessoas0 || []), ...(pessoas1 || [])];
            const unique = merged.filter(
              (v, i, a) => a.findIndex((x) => x.codigo_dms_pessoa === v.codigo_dms_pessoa) === i
            );
            setPessoas(unique);
          } catch (err) {
            console.error("Erro ao buscar pessoas do DMS:", err);
          }
        } else {
          setIsDealer(false);
          setPessoas([]);
        }
      } catch (error) {
        toast.error("Erro ao buscar usuário!");
        console.error("Erro ao buscar usuário:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchEmpresas = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const empresasData = await getEmpresas(token);
        setEmpresas(empresasData);
      } catch (error) {
        toast.error("Erro ao buscar empresas!");
        console.error("Erro ao buscar empresas:", error);
      }
    };

    const fetchTiposUsuario = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const tiposUsuarioData = await getTipo(token);
        setAllTipos(tiposUsuarioData);
      } catch (error) {
        toast.error("Erro ao buscar tipos de usuário!");
        console.error("Erro ao buscar tipos de usuário:", error);
      }
    };

    fetchEmpresas();
    fetchUsuario();
    fetchTiposUsuario();
  }, [id]);

  useEffect(() => {
    if (allTipos.length === 0) return;
    const tiposDisponiveis = allTipos.filter(
      (tipoDisponivel: Tipo) => !tipo.some((tipoCadastrado) => tipoCadastrado.id === tipoDisponivel.id)
    );
    const prevIds = tiposUsuario.map((t) => t.id).join(",");
    const nextIds = tiposDisponiveis.map((t) => t.id).join(",");
    if (prevIds !== nextIds) setTiposUsuario(tiposDisponiveis);
  }, [allTipos, tipo]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      const formData = new FormData();
      formData.append("nome_usuario", nomeUsuario);
      formData.append("login_usuario", loginUsuario);
      formData.append("assinatura_usuario", assinaturaUsuario || "");
      formData.append(
        "telefone_usuario",
        telefoneUsuario || ""
      );
      const empresaId = empresa ? String(empresa.id) : String(idEmpresa);
      formData.append("id_empresa", empresaId);
      if (senhaUsuario && senhaUsuario.trim() !== "") {
        formData.append("senha_usuario", senhaUsuario);
      }

      await updateUsuario(token, id!, formData);

      if (tiposUsuarioSelecionados && tiposUsuarioSelecionados.length > 0) {
        await Promise.all(
          tiposUsuarioSelecionados.map(async (tipo: Tipo) => {
            await createTipoUsuario(token, id!, tipo.id);
          })
        );
      }

      toast.success("Usuário atualizado com sucesso!");
      navigate("/usuarios");
    } catch (error) {
      toast.error("Erro ao atualizar usuário!");
      console.error("Erro ao atualizar usuário:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTipo = async (tipoId: number) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }
      await deleteTipoUser(token, tipoId);

      const tiposAtualizados = tipo.filter((tipo) => tipo.id !== tipoId);
      setTipo(tiposAtualizados);
      const tiposDisponiveis = allTipos.filter(
        (tipoDisponivel: Tipo) =>
          !tiposAtualizados.some((tipoCadastrado) => tipoCadastrado.id === tipoDisponivel.id)
      );
      setTiposUsuario(tiposDisponiveis);
      window.location.reload();
      toast.success("Tipo excluído com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir tipo!");
      console.error("Erro ao excluir tipo:", error);
    }
  };

  const handleBack = () => {
    navigate("/usuarios");
  };

  const handleEmpresaChange = (event: React.SyntheticEvent, value: Empresa | null) => {
    setEmpresa(value);
  };

  const handlePessoaChange = (event: React.SyntheticEvent, value: Pessoa | null) => {
    setSelectedPessoa(value);
    if (value) {
      setNomeUsuario(value.nome_pessoa);
    }
  };

  const handleVincularPessoa = async () => {
    if (!selectedPessoa) {
      toast.error("Selecione uma pessoa para vincular.");
      return;
    }
    if (!id) {
      toast.error("ID do usuário não disponível.");
      return;
    }
    setLinking(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token não encontrado");

      const payload = {
        Usuario_Codigo: selectedPessoa.codigo_dms_pessoa,
        Usuario_Nome: selectedPessoa.nome_pessoa,
        Usuario_Identificador: selectedPessoa.identificador_pessoa,
        Usuario_DocIdentificador: selectedPessoa.documento_pessoa,
        id_usuario: Number(id),
      };

      await vincularPessoa(token, payload);
      toast.success("Usuário vinculado ao DMS com sucesso!");
    } catch (error) {
      toast.error("Erro ao vincular usuário ao DMS.");
      console.error("Erro ao vincular pessoa:", error);
    } finally {
      setLinking(false);
    }
  };

  const handleTipoUsuarioChange = (
    event: React.SyntheticEvent,
    value: Tipo[]
  ) => {
    setTiposUsuarioSelecionados(value);
  };

  const fields: FormField[] = [];
  if (hasPessoa) {
    fields.push({
      label: "Aviso DMS",
      type: "custom",
      component: (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Usuário já possui um registro vinculado ao DMS. Para alterar o vínculo, remova o vínculo atual no DMS.
          </Typography>
        </Box>
      ),
    });
  } else {
    if (isDealer) {
      fields.push({
        label: "Nome do Usuário",
        type: "custom",
        component: (
          <Autocomplete
            key="pessoa-autocomplete"
            options={pessoas}
            getOptionLabel={(option: Pessoa) => `${option.nome_pessoa} - ${option.documento_pessoa}`}
            filterOptions={(options, { inputValue }) =>
              options.filter(
                (o) =>
                  o.nome_pessoa.toLowerCase().includes(inputValue.toLowerCase()) ||
                  o.documento_pessoa.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            value={selectedPessoa}
            onChange={handlePessoaChange}
            renderInput={(params) => (
              <TextField {...params} label="Nome do Usuário" variant="outlined" fullWidth sx={{ mb: 2 }} required />
            )}
          />
        ),
      });
    } else {
      fields.push({
        label: "Nome do Usuário",
        type: "text",
        value: nomeUsuario,
        required: true,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNomeUsuario(e.target.value),
      });
    }
  }

  fields.push({
    label: "Login",
    type: "text",
    value: loginUsuario,
    required: true,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setLoginUsuario(e.target.value),
  });

  fields.push({
    label: "Nova Senha",
    type: "text",
    value: senhaUsuario,
    required: false,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSenhaUsuario(e.target.value),
  });

  fields.push({
    label: "Assinatura do Usuário",
    type: "text",
    value: assinaturaUsuario,
    required: false,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setAssinaturaUsuario(e.target.value),
  });

  fields.push({
    label: "Empresa",
    type: "custom",
    component: (
      <Autocomplete
        options={empresas}
        getOptionLabel={(option: Empresa) => option.nome_empresa}
        value={empresa}
        onChange={handleEmpresaChange}
        sx={{ marginBottom: 2 }}
        renderInput={(params) => <TextField {...params} label="Empresa" variant="outlined" />}
      />
    ),
  });

  fields.push({
    label: "Telefone do Usuário",
    type: "text",
    value: telefoneUsuario,
    required: false,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTelefoneUsuario(e.target.value),
  });

  fields.push({
    label: "Tipos de Usuário",
    type: "autocomplete",
    value: tiposUsuarioSelecionados,
    required: false,
    onChangeAutocomplete: (event: React.SyntheticEvent, value: unknown) =>
      handleTipoUsuarioChange(event, value as Tipo[]),
    options: tiposUsuario,
    getOptionLabel: (option: unknown) => (option as Tipo).nome_tipo,
    multiple: true,
  });

  return (
    <>
      <Formulario
        title="Editar Usuário"
        fields={fields}
        onSubmit={handleSubmit}
        onBack={handleBack}
        loading={loading || updating}
        secondaryAction={
          isDealer && !hasPessoa
            ? {
                label: "Vincular Usuário ao DMS",
                onClick: handleVincularPessoa,
                disabled: !selectedPessoa || linking,
                color: "secondary",
              }
            : undefined
        }
      />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Empresa Cadastrada:</Typography>
        {empresa ? (
          <TableContainer component={Box} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>CNPJ da Empresa</TableCell>
                  <TableCell>Nome da Empresa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{empresa.id}</TableCell>
                  <TableCell>{empresa.cnpj_empresa}</TableCell>
                  <TableCell>{empresa.nome_empresa}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1" mt={2}>
            Nenhuma empresa cadastrada para esse usuário.
          </Typography>
        )}

        <Typography variant="h6" mt={4}>
          Tipos Cadastrados:
        </Typography>
        {tipo.length === 0 ? (
          <Typography variant="body1" mt={2}>
            Nenhum tipo cadastrado para esse usuário.
          </Typography>
        ) : (
          <TableContainer component={Box} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome do Tipo</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tipo.map((tipo: Tipo) => (
                  <TableRow key={tipo.id}>
                    <TableCell>{tipo.id}</TableCell>
                    <TableCell>{tipo.nome_tipo}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteTipo(tipo.pivot.id)}
                      >
                        Deletar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};

export default EditarUsuario;
